import { Box } from '@chakra-ui/react';
import {
  ClientRect,
  closestCorners,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DraggableAttributes,
  DraggableSyntheticListeners,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dispatch, ReactNode, SetStateAction, useRef, useState } from 'react';
import { MdOutlineDragIndicator } from 'react-icons/md';
import {
  everyGroupAtRoot,
  findContainerId,
  isContainerId,
  isRootRow,
  LayoutRecord,
  moveToRootIndex,
  PointerDelta,
  recordSignature,
  ROOT_ID,
  rootRowId,
  sameIdOrder,
  toContainerId,
  travelledFar,
} from './VDraggable.helpers';

const REORDER_GROUP_LABEL = 'Reorder group';
const REORDER_ITEM_LABEL = 'Reorder item';
const REORDER_ROW_LABEL = 'Reorder row';
const ROOT_BOUNDARY_BAND_PX = 12;
const ROOT_BAND_AFTER_LIFT_PX = 32;

const midpoint = (rect: ClientRect): number => rect.top + rect.height / 2;

const insertsAfter = (event: DragOverEvent | DragEndEvent): boolean => {
  const dragged = event.active.rect.current.translated;
  if (!dragged || !event.over) {
    return false;
  }

  return midpoint(dragged) > midpoint(event.over.rect);
};

const useStableIds = (ids: string[]): string[] => {
  const [stableIds, setStableIds] = useState(ids);
  if (!sameIdOrder(stableIds, ids)) {
    setStableIds(ids);
  }

  return stableIds;
};

const DRAG_HANDLE_ICON_SIZE = 16;

type DragHandleProps = {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (_element: HTMLElement | null) => void;
  label: string;
};

const DragHandle = ({ attributes, listeners, setActivatorNodeRef, label }: DragHandleProps) => (
  <Box
    as="span"
    ref={setActivatorNodeRef}
    {...attributes}
    {...listeners}
    role="button"
    aria-label={label}
    cursor="grab"
    mr="2"
    _active={{ cursor: 'grabbing' }}
  >
    <MdOutlineDragIndicator size={DRAG_HANDLE_ICON_SIZE} />
  </Box>
);

const OverlayDragHandle = ({ label }: { label: string }) => (
  <Box as="span" role="button" aria-label={label} cursor="grabbing" mr="2">
    <MdOutlineDragIndicator size={DRAG_HANDLE_ICON_SIZE} />
  </Box>
);

type RenderRow<T, I> = (
  _entry: T | I,
  _content: ReactNode,
  _dragHandle: ReactNode,
  _isDragging: boolean,
  _isPreview: boolean
) => ReactNode;

type NestedProps<T, I> = {
  containers: T[];
  getId: (_node: T | I) => string;
  isContainer: (_container: T) => boolean;
  getItems: (_container: T) => I[];
  setItems: (_container: T, items: I[]) => T;
  onChange: Dispatch<SetStateAction<T[]>>;
  onDrop?: (_next: T[]) => void;
  renderItem: (_item: T | I) => ReactNode;
  renderEmptyContainer?: (_container: T) => ReactNode;
  children: RenderRow<T, I>;
};

type SortableRowProps<T, I> = {
  id: string;
  node: T | I;
  content: ReactNode;
  label: string;
  children: RenderRow<T, I>;
};

const SortableRow = <T, I>({ id, node, content, label, children }: SortableRowProps<T, I>) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  const dragHandle = (
    <DragHandle
      attributes={attributes}
      listeners={listeners}
      setActivatorNodeRef={setActivatorNodeRef}
      label={label}
    />
  );

  return (
    <Box
      ref={setNodeRef}
      style={style}
      display="flow-root"
      data-vdraggable-row=""
      data-dragging={isDragging || undefined}
    >
      {children(node, content, dragHandle, isDragging, false)}
    </Box>
  );
};

type GroupZoneProps<T, I> = Pick<
  NestedProps<T, I>,
  'getId' | 'renderItem' | 'renderEmptyContainer' | 'children'
> & {
  group: T;
  groupId: string;
  items: I[];
};

const GroupZone = <T, I>({
  group,
  groupId,
  items,
  getId,
  renderItem,
  renderEmptyContainer,
  children,
}: GroupZoneProps<T, I>) => {
  const { setNodeRef } = useDroppable({ id: toContainerId(groupId) });
  const itemIds = useStableIds(items.map(getId));

  return (
    <Box ref={setNodeRef} minH="8">
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {items.length === 0
          ? (renderEmptyContainer?.(group) ?? null)
          : items.map((item) => (
              <SortableRow
                key={getId(item)}
                id={getId(item)}
                node={item}
                content={renderItem(item)}
                label={REORDER_ITEM_LABEL}
              >
                {children}
              </SortableRow>
            ))}
      </SortableContext>
    </Box>
  );
};

const VDraggable = <T, I>({
  containers,
  getId,
  isContainer,
  getItems,
  setItems,
  onChange,
  onDrop,
  renderItem,
  renderEmptyContainer,
  children,
}: NestedProps<T, I>) => {
  const rootIds = useStableIds(containers.map(getId));
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const buildRecord = (rows: T[]): LayoutRecord => {
    const record: LayoutRecord = { [ROOT_ID]: rows.map(getId) };
    rows.forEach((row) => {
      if (isContainer(row)) {
        record[toContainerId(getId(row))] = getItems(row).map(getId);
      }
    });

    return record;
  };

  const buildNodeById = (rows: T[]): Map<string, T | I> => {
    const nodeById = new Map<string, T | I>();
    rows.forEach((row) => {
      nodeById.set(getId(row), row);
      if (isContainer(row)) {
        getItems(row).forEach((item) => nodeById.set(getId(item), item));
      }
    });

    return nodeById;
  };

  const rebuild = (record: LayoutRecord, nodeById: Map<string, T | I>): T[] =>
    record[ROOT_ID].map((rootId) => {
      const node = nodeById.get(rootId) as T;
      const containerKey = toContainerId(rootId);
      if (!(containerKey in record)) {
        return node;
      }
      const items = record[containerKey].map((itemId) => nodeById.get(itemId) as I);

      return setItems(node, items);
    });

  const insertIndex = (target: string[], overId: string, containerId: string): number => {
    if (overId === containerId || isContainerId(overId)) {
      return target.length;
    }
    const index = target.indexOf(overId);

    return index === -1 ? target.length : index;
  };

  const moveBetween = (
    record: LayoutRecord,
    activeId: string,
    from: string,
    to: string,
    overId: string
  ): LayoutRecord => {
    const fromItems = record[from].filter((id) => id !== activeId);
    const toItems = [...record[to]];
    toItems.splice(insertIndex(toItems, overId, to), 0, activeId);

    return { ...record, [from]: fromItems, [to]: toItems };
  };

  const reorderWithin = (
    record: LayoutRecord,
    containerId: string,
    activeId: string,
    overId: string
  ): LayoutRecord => {
    const items = record[containerId];
    const oldIndex = items.indexOf(activeId);
    const newIndex =
      overId === containerId || isContainerId(overId) ? items.length - 1 : items.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      return record;
    }

    return { ...record, [containerId]: arrayMove(items, oldIndex, newIndex) };
  };

  const moveIntoContainer = (
    record: LayoutRecord,
    activeId: string,
    overId: string
  ): LayoutRecord => {
    const from = findContainerId(record, activeId);
    const to = findContainerId(record, overId);
    if (!from || !to || from === to) {
      return record;
    }

    return moveBetween(record, activeId, from, to, overId);
  };

  const liftToRoot = (
    record: LayoutRecord,
    activeId: string,
    overRowId: string,
    event: DragOverEvent | DragEndEvent
  ): LayoutRecord => {
    const from = findContainerId(record, activeId);
    if (!from || from === ROOT_ID) {
      return record;
    }
    const index = record[ROOT_ID].indexOf(overRowId) + (insertsAfter(event) ? 1 : 0);

    return moveToRootIndex(record, activeId, from, index);
  };

  const dropAtRootRow = (
    record: LayoutRecord,
    activeId: string,
    overRowId: string,
    event: DragEndEvent
  ): LayoutRecord => {
    if (findContainerId(record, activeId) === ROOT_ID) {
      return reorderWithin(record, ROOT_ID, activeId, overRowId);
    }

    return liftToRoot(record, activeId, overRowId, event);
  };

  const workingRecordRef = useRef<LayoutRecord | null>(null);
  const nodeByIdRef = useRef<Map<string, T | I>>(new Map());
  const snapshotRef = useRef<T[] | null>(null);
  const activeIsGroupRef = useRef(false);
  const previewDeltaRef = useRef<PointerDelta | null>(null);
  const [activeOverlay, setActiveOverlay] = useState<{ node: T | I; isGroup: boolean } | null>(
    null
  );

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    const record = buildRecord(containers);
    const nodeById = buildNodeById(containers);
    workingRecordRef.current = record;
    nodeByIdRef.current = nodeById;
    snapshotRef.current = containers;
    previewDeltaRef.current = null;

    const isGroup = toContainerId(activeId) in record;
    activeIsGroupRef.current = isGroup;

    const node = nodeById.get(activeId);
    setActiveOverlay(node ? { node, isGroup } : null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const record = workingRecordRef.current;
    const { active, over } = event;
    if (!record || !over || activeIsGroupRef.current) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);
    const next = isRootRow(record, overId)
      ? liftToRoot(record, activeId, overId, event)
      : moveIntoContainer(record, activeId, overId);
    if (next === record) {
      return;
    }
    if (!travelledFar(event.delta, previewDeltaRef.current)) {
      return;
    }

    previewDeltaRef.current = event.delta;
    workingRecordRef.current = next;
    onChange(rebuild(next, nodeByIdRef.current));
  };

  const applyDrop = (record: LayoutRecord, event: DragEndEvent): LayoutRecord => {
    const { active, over } = event;
    if (!over) {
      return record;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeIsGroupRef.current) {
      const target = rootRowId(record, overId);

      return target ? reorderWithin(record, ROOT_ID, activeId, target) : record;
    }

    if (isRootRow(record, overId)) {
      return dropAtRootRow(record, activeId, overId, event);
    }

    const from = findContainerId(record, activeId);
    const to = findContainerId(record, overId);
    if (!from || !to) {
      return record;
    }

    if (from === to) {
      return reorderWithin(record, from, activeId, overId);
    }

    return moveBetween(record, activeId, from, to, overId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const record = workingRecordRef.current;
    const snapshot = snapshotRef.current;
    workingRecordRef.current = null;
    snapshotRef.current = null;
    setActiveOverlay(null);
    if (!record) {
      return;
    }

    const dropped = applyDrop(record, event);
    const next = everyGroupAtRoot(dropped) ? dropped : record;
    const finalOrder = rebuild(next, nodeByIdRef.current);
    onChange(finalOrder);

    const changed = !snapshot || recordSignature(buildRecord(snapshot)) !== recordSignature(next);
    if (changed) {
      onDrop?.(finalOrder);
    }
  };

  const handleDragCancel = () => {
    const snapshot = snapshotRef.current;
    workingRecordRef.current = null;
    snapshotRef.current = null;
    setActiveOverlay(null);
    if (snapshot) {
      onChange(snapshot);
    }
  };

  const collisionDetection: CollisionDetection = (args) => {
    const rootIds = new Set(containers.map(getId));
    const onlyRootRows = () =>
      closestCorners({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) =>
          rootIds.has(String(container.id))
        ),
      });

    if (activeIsGroupRef.current) {
      return onlyRootRows();
    }

    const pointerY = args.pointerCoordinates?.y;
    const rootRects = containers
      .map((row) => args.droppableRects.get(getId(row)))
      .filter((rect): rect is ClientRect => rect !== undefined);
    if (pointerY === undefined || rootRects.length === 0) {
      return closestCorners(args);
    }

    const record = workingRecordRef.current;
    const alreadyLifted = record ? isRootRow(record, String(args.active.id)) : false;
    const band = alreadyLifted ? ROOT_BAND_AFTER_LIFT_PX : ROOT_BOUNDARY_BAND_PX;

    const nearRowEdge = rootRects.some(
      (rect) => Math.abs(pointerY - rect.top) <= band || Math.abs(pointerY - rect.bottom) <= band
    );
    const outsideList =
      pointerY < rootRects[0].top || pointerY > rootRects[rootRects.length - 1].bottom;

    return nearRowEdge || outsideList ? onlyRootRows() : closestCorners(args);
  };

  const renderOverlay = () => {
    if (!activeOverlay) {
      return null;
    }
    const { node, isGroup } = activeOverlay;
    const content = isGroup ? null : renderItem(node);
    const label = isGroup ? REORDER_GROUP_LABEL : REORDER_ROW_LABEL;

    return children(node, content, <OverlayDragHandle label={label} />, true, true);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
        {containers.map((row) => {
          const id = getId(row);
          if (!isContainer(row)) {
            return (
              <SortableRow
                key={id}
                id={id}
                node={row}
                content={renderItem(row)}
                label={REORDER_ROW_LABEL}
              >
                {children}
              </SortableRow>
            );
          }

          const itemsSlot = (
            <GroupZone
              group={row}
              groupId={id}
              items={getItems(row)}
              getId={getId}
              renderItem={renderItem}
              renderEmptyContainer={renderEmptyContainer}
            >
              {children}
            </GroupZone>
          );

          return (
            <SortableRow
              key={id}
              id={id}
              node={row}
              content={itemsSlot}
              label={REORDER_GROUP_LABEL}
            >
              {children}
            </SortableRow>
          );
        })}
      </SortableContext>
      <DragOverlay>{renderOverlay()}</DragOverlay>
    </DndContext>
  );
};

export default VDraggable;
